import React, {
	type PropsWithChildren,
	createContext,
	useEffect,
	useState,
} from "react";
import ReactModal from "react-modal";
import { AddressPicker } from "./AddressManager.Inputs";
import {
	LOQATE_ADDRESS_INTERACTIVE_URL,
	LOQATE_ADDRESS_RETRIEVE_URL,
} from "./AddressManager.consts";

export const AddressManagerContext = createContext<AddressManagerContextProps>({
	confirmedAddress: null,
	queryAddresses: () => {},
	addressResults: [],
	querySingleAddress: () => {},
	showAddressPicker: false,
	hideAddressPicker: () => {},
});

const AddressManagerProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [showAddressPicker, setShowAddressPicker] = useState(false);
	const [addressSearchValue, setSearchValue] = useState<string | null>(null);
	const [postcodeResults, setPostcodeResults] = useState<LoqateItems[]>([]);
	const [addressResults, setAddressResults] = useState<LoqateItems[]>([]);
	const [addressData, setAddressData] = useState<LoqateAddress | null>(null);
	const [confirmedAddress, setConfirmedAddress] = useState<AddressData | null>(
		null,
	);

	/**
	 *  Give a search term (which should be the postcode) this uses loqate
	 *  to query for addresses. It will return eithe ADDRESS or POSTCODE
	 *  ADDRESS = a specific address which we can use.
	 *  POSTCODE = a postcode which we can use to query for addresses.
	 * @param searchValue  The postcode to search for.
	 */
	const queryAddresses = async (searchValue: string) => {
		setShowAddressPicker(true);
		const res = await fetch(
			`${LOQATE_ADDRESS_INTERACTIVE_URL}&Text=${searchValue}`,
		).then((res) => res.json());
		setSearchValue(searchValue);
		if (res.Items && Array.isArray(res.Items)) {
			setPostcodeResults(
				res.Items.filter((item: LoqateItems) => item.Type === "Postcode"),
			);
			setAddressResults(
				res.Items.filter((item: LoqateItems) => item.Type === "Address"),
			);
		}
	};

	/**
	 * This will query loqate for a single address based on the addressId
	 * which is returned from the queryAddresses function.
	 *
	 * It will return an object which contains all the fields we need
	 * @param addressId The addressId to query for.
	 */
	const querySingleAddress = async (addressId: string) => {
		try {
			const res: { Items: LoqateAddress[] } = await fetch(
				`${LOQATE_ADDRESS_RETRIEVE_URL}&Id=${addressId}`,
			).then((res) => res.json());

			if (res?.Items?.length > 0) {
				const address = res.Items[0];
				setAddressData(address);
			}
		} catch (e) {
			console.error("querySingleAddress ERROR: ", e);
		}
	};

	useEffect(() => {
		ReactModal.setAppElement("body");
	}, []);

	useEffect(() => {
		if (addressData) {
			setConfirmedAddress({
				AddressLine1: addressData.Line1 || "",
				AddressLine2: addressData.Line2 || "",
				TownCity: addressData.City || "",
				County: addressData.AdminAreaName || "",
				Country: addressData.CountryName || "",
				Postcode: addressData.PostalCode || "",
			});
			setShowAddressPicker(false);
		}
	}, [addressData]);

	useEffect(() => {
		const queryAllPostcodes = async () => {
			const queryPostcode = async (searchValue: string, container: string) => {
				const res = await fetch(
					`${LOQATE_ADDRESS_INTERACTIVE_URL}&Text=${searchValue}&Container=${container}`,
				).then((res) => res.json());
				if (res.Items?.length > 0) {
					setAddressResults((currAddressResults) => [
						...currAddressResults,
						...res.Items.filter((item: LoqateItems) => item.Type === "Address"),
					]);
				}
			};

			await Promise.all(
				postcodeResults.map((postcode) =>
					queryPostcode(addressSearchValue, postcode.Id),
				),
			);
		};
		if (postcodeResults?.length > 0) queryAllPostcodes();
	}, [postcodeResults, addressSearchValue]);

	return (
		<AddressManagerContext.Provider
			value={{
				confirmedAddress,
				queryAddresses,
				querySingleAddress,
				addressResults,
				showAddressPicker,
				hideAddressPicker: () => setShowAddressPicker(false),
			}}
		>
			{children}
			<AddressPicker />
		</AddressManagerContext.Provider>
	);
};

export default AddressManagerProvider;
